import React, {
  createContext,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { FormProvider, useFieldArray } from "react-hook-form";

import type { JSX } from "react";
import type { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";

import type { LanguageCode } from "../api/ProjectAPI.types";

export interface FormTranslationsHandle<TApiResult = unknown> {
  isValid: () => Promise<boolean>;
  onSaveSuccess: () => void;
  getApiTranslations?: () => TApiResult;
  isDirty: () => boolean;
}

export interface PageLanguageField {
  language: LanguageCode;
  default: boolean;
  id?: string;
  slug?: string;
}

/** T is the full translation field type for a given page (must extend PageLanguageField) */
export interface PageFieldArray<
  T extends PageLanguageField = PageLanguageField,
> {
  translations: T[];
}

interface FormTranslationsContextValue {
  fieldArray: UseFieldArrayReturn<PageFieldArray, "translations"> | null;
  methods: UseFormReturn<PageFieldArray, unknown, PageFieldArray> | null;
  activeLanguageIndex: number;
  /** Calls the parent onAddLanguage, normalizes the response via toFormField, appends to the field array and navigates to the new tab. */
  addLanguage: ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }) => Promise<void>;
  /** True while addLanguage is in flight, so callers can lock their trigger. */
  isAddingLanguage: boolean;
  onDeleteLanguage: (
    updatedTranslations: PageLanguageField[],
    removedTranslation: PageLanguageField,
  ) => void;
  setActiveLanguageIndex: (index: number) => void;
  getSetAsDefaultDisabledReason?: (
    translation: PageLanguageField,
  ) => string | undefined;
}

export const FormTranslationsContext =
  createContext<FormTranslationsContextValue>({
    fieldArray: null,
    methods: null,
    activeLanguageIndex: 0,
    addLanguage: async () => {},
    isAddingLanguage: false,
    onDeleteLanguage: () => {},
    setActiveLanguageIndex: () => {},
  });

interface FormProviderWithTranslationsProps<
  T extends PageLanguageField,
  TApiResult = unknown,
  TResponse = Record<string, unknown>,
> {
  children: React.ReactNode;
  onActiveLanguageIndexChange?: (index: number) => void;
  /**
   * Creates a new translation on the server. Returns the API response which is
   * then normalized via toFormField before being appended to the field array.
   */
  onAddLanguage: ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }) => Promise<TResponse | null> | TResponse | null;
  /**
   * Adapts the raw API response from onAddLanguage into the form field shape T
   * before it is appended to the field array. When omitted the raw response is
   * spread directly (legacy behaviour).
   */
  toFormField?: (response: TResponse, language: LanguageCode) => T;
  /**
   * Called when a language is removed, with the remaining translations as form
   * field values and the one being removed. Entities whose API takes the whole
   * array only need the first; those with a per translation DELETE endpoint need
   * the second, which only this component can know.
   */
  onDeleteLanguage?: (updatedTranslations: T[], removedTranslation: T) => void;
  methods: UseFormReturn<PageFieldArray<T>, unknown, PageFieldArray<T>>;
  /**
   * Converts the current form translations to the API payload shape.
   * When provided, the handle exposed via handleRef will include getApiTranslations().
   */
  formToApiTranslations?: (translations: T[]) => TApiResult;
  /**
   * Optional ref to expose the form handle (isValid, onSaveSuccess, getApiTranslations) to the parent.
   */
  handleRef?: React.Ref<FormTranslationsHandle<TApiResult>>;
  /**
   * Returns why the active translation cannot become the default language, or
   * undefined when it can. The string is displayed as-is in a tooltip on the
   * disabled action, so the caller returns it already translated.
   */
  getSetAsDefaultDisabledReason?: (translation: T) => string | undefined;
}

export function FormProviderWithTranslations<
  T extends PageLanguageField,
  TApiResult = unknown,
  TResponse = Record<string, unknown>,
>({
  children,
  onAddLanguage,
  toFormField,
  onDeleteLanguage,
  methods,
  formToApiTranslations,
  handleRef,
  getSetAsDefaultDisabledReason,
}: FormProviderWithTranslationsProps<T, TApiResult, TResponse>): JSX.Element {
  const fieldArray = useFieldArray({
    control:
      methods.control as unknown as UseFormReturn<PageFieldArray>["control"],
    name: "translations",
  });

  const [activeLanguageIndex, setActiveLanguageIndex] = useState(0);
  const [isAddingLanguage, setIsAddingLanguage] = useState(false);

  const { isDirty } = methods.formState;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useImperativeHandle(handleRef, () => ({
    isDirty: (): boolean => isDirtyRef.current,
    isValid: async (): Promise<boolean> => {
      const valid = await methods.trigger();
      if (!valid) {
        const { errors } = methods.formState;
        const translations = methods.getValues("translations");
        const firstErrorIndex = (translations as PageLanguageField[]).findIndex(
          (_, i) =>
            (errors as { translations?: Record<number, unknown> })
              .translations?.[i],
        );
        if (firstErrorIndex !== -1) {
          setActiveLanguageIndex(firstErrorIndex);

          const errorFieldName = Object.keys(
            (errors as { translations?: Record<number, unknown> })
              .translations?.[firstErrorIndex] ?? {},
          )[0];

          if (errorFieldName) {
            methods.setFocus(
              `translations.${firstErrorIndex}.${errorFieldName}` as Parameters<
                typeof methods.setFocus
              >[0],
            );
          }
        }
      }
      return valid;
    },
    setActiveLanguageIndex: (index: number): void =>
      setActiveLanguageIndex(index),
    onSaveSuccess: (): void => {
      methods.reset(methods.getValues());
    },
    ...(formToApiTranslations && {
      getApiTranslations: (): TApiResult =>
        formToApiTranslations(methods.getValues("translations")),
    }),
  }));

  useEffect(() => {
    const { length } = fieldArray.fields;
    if (activeLanguageIndex >= length && length > 0) {
      setActiveLanguageIndex(length - 1);
    }
  }, [fieldArray.fields, activeLanguageIndex]);

  const addLanguage = useCallback(
    async ({
      languageCode,
      languageUuid,
    }: {
      languageCode: LanguageCode;
      languageUuid: string;
    }): Promise<void> => {
      setIsAddingLanguage(true);

      try {
        const response = await onAddLanguage({ languageCode, languageUuid });

        if (!response) return;

        const newField = toFormField
          ? toFormField(response, languageCode)
          : ({ ...response, language: languageCode } as unknown as T);

        const newIndex = fieldArray.fields.length;

        fieldArray.append(newField as unknown as PageLanguageField);

        setActiveLanguageIndex(newIndex);

        // The new translation was saved to the server (empty), so reset its default
        // value to the current value to prevent it from showing as dirty.
        methods.resetField(
          `translations.${newIndex}` as Parameters<
            typeof methods.resetField
          >[0],
          { defaultValue: newField } as Parameters<
            typeof methods.resetField
          >[1],
        );
      } finally {
        setIsAddingLanguage(false);
      }
    },
    [onAddLanguage, toFormField, fieldArray, setActiveLanguageIndex, methods],
  );

  const value = useMemo(
    () =>
      ({
        methods,
        fieldArray,
        activeLanguageIndex,
        addLanguage,
        isAddingLanguage,
        onDeleteLanguage,
        setActiveLanguageIndex,
        getSetAsDefaultDisabledReason,
      }) as unknown as FormTranslationsContextValue,
    [
      methods,
      fieldArray,
      activeLanguageIndex,
      addLanguage,
      isAddingLanguage,
      onDeleteLanguage,
      getSetAsDefaultDisabledReason,
    ],
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <FormTranslationsContext.Provider value={value}>
        {children}
      </FormTranslationsContext.Provider>
    </FormProvider>
  );
}
